import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Service } from 'webapp-types';
import { BlockVisualization, DataSelectType, ShownLabels, TimeUnit } from 'webapp/enums/enums';
import { getFormattedTime, hashCode } from 'webapp/utils/number';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import DChartSeries from 'webapp/d-chart/series/series';

const RecordLimit = 1;

export default class VisualizationHeatmap extends VisualizationBase {
	@service('account') declare accountService: Service.Account;

	visualizationType = BlockVisualization.HEATMAP;

	recordLimit = RecordLimit;

	get actualItemsInLine() {
		return this.block.itemsInLine || this.itemsInLineFromGranulation;
	}

	get showValues() {
		return this.blockSetup.firstShownDataSelect?.shownLabels === ShownLabels.VALUE;
	}

	get output() {
		return this.blockSetup.firstShownDataSelect?.dataRoot?.outputs[0] || null;
	}

	get granulationTimeUnit(): TimeUnit {
		return this.blockSetup.limitations.dateRange?.granulationTimeUnit || TimeUnit.DAY;
	}

	// --------- Data ---------

	@tracked series: DChartSeries | null = null;

	override updateDataCache() {
		this.series = this.getSeries();
	}

	override clearDataCache() {
		this.series = null;
	}

	private getSeries(): DChartSeries | null {
		const output = this.output;

		if (!output) {
			return null;
		}

		const series = new DChartSeries({
			type: DataSelectType.MAIN,
			timezone: this.dataTimezone,
			points: this.createDChartPointsFromOutput(output),
			granulationLabels: output.dataRoot.granulationLabels,
		});

		return series;
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				l: this.actualItemsInLine,
				va: dss.map((ds) => ds.shownLabels),
			}),
		);
	}

	// ------- x-axis helpers --------

	get isItemsInLineFromGranulation() {
		const itemsInLine = this.block.itemsInLine;
		return !itemsInLine || itemsInLine === this.itemsInLineFromGranulation;
	}

	get itemsInLineFromGranulation(): number {
		switch (this.granulationTimeUnit) {
			case TimeUnit.HOUR:
				return 8; // 1 day will be 3 lines
			case TimeUnit.WEEK:
				return 4; // line = month (approx)
			case TimeUnit.MONTH:
				return 6; // line = half a year
			case TimeUnit.YEAR:
				return 5; // line = 5 years
			case TimeUnit.DAY:
			default:
				return 7; // line = week
		}
	}

	get showXAxisLabels() {
		// true if we have logical rows e.g. days of week, months of year, etc.
		// TODO: support more than just days of week
		const granulationTimeUnit = this.granulationTimeUnit;
		return this.isItemsInLineFromGranulation && granulationTimeUnit === TimeUnit.DAY;
	}

	get xAxisLabels() {
		if (!this.showXAxisLabels) {
			return [];
		}

		const firstTimestamp =
			this.customFirstItemTimestamp || this.output?.dataRoot.granulationLabels[0]?.ts;

		if (firstTimestamp) {
			// we can't read from the series bcos the labels are formatted differently
			// e.g. for daily granulation they show date, we need day of the week
			const labels: string[] = [];
			for (let i = 0; i < this.actualItemsInLine; i++) {
				// NOTE: we only support days here - see this.showXAxisLabels above
				const granulationString = 'days',
					format = 'ddd';

				const momentInstance = moment.unix(firstTimestamp).tz(this.dataTimezone),
					pointTimestamp = momentInstance.add(i, granulationString).unix(),
					label = getFormattedTime(pointTimestamp, this.dataTimezone, format);
				labels.push(label);
			}
			return labels;
		}

		return [];
	}

	get startFillerCount() {
		let fillerCount = 0;

		const customFirstTs = this.customFirstItemTimestamp;
		if (customFirstTs) {
			const actualFirstTs = this.output?.dataRoot.granulationLabels[0]?.ts;
			if (actualFirstTs) {
				const granulationTimeUnit = this.granulationTimeUnit;
				let currentTs = customFirstTs;
				while (currentTs < actualFirstTs) {
					fillerCount++;

					// NOTE: only supporting days and hours atm - check this.customFirstItemTimestamp
					let granulationString = '';
					if (granulationTimeUnit === TimeUnit.DAY) {
						granulationString = 'days';
					} else if (granulationTimeUnit === TimeUnit.HOUR) {
						granulationString = 'hours';
					}
					currentTs = moment
						.unix(currentTs)
						.tz(this.dataTimezone)
						.add(1, granulationString as moment.unitOfTime.DurationConstructor)
						.unix();
				}
			}
		}
		return fillerCount;
	}

	get customFirstItemTimestamp(): number | null {
		// if we have starting fillers, this returns the first ts
		// which differs from the first ts in the series
		const granulationTimeUnit = this.granulationTimeUnit;
		if (
			this.isItemsInLineFromGranulation &&
			[TimeUnit.HOUR, TimeUnit.DAY].includes(granulationTimeUnit)
		) {
			// find first timestamp in granulationLabels where label === if we format this ts on FE
			// this might not be the first item bcos of different formatting rules for "edge" items
			const itemsInLine = this.actualItemsInLine;

			const firstMatchingTs = (this.output?.dataRoot.granulationLabels || []).find(
				(granulationData, idx) => {
					return (
						idx < itemsInLine &&
						granulationData.label === this.formatGranulationLabel(granulationData.ts)
					);
				},
			)?.ts;

			if (firstMatchingTs) {
				const firstTs = this.output?.dataRoot.granulationLabels[0]?.ts,
					momentInstance = moment.unix(firstMatchingTs).tz(this.dataTimezone);
				if (granulationTimeUnit === TimeUnit.HOUR) {
					return momentInstance.startOf('day').unix();
				} else if (granulationTimeUnit === TimeUnit.DAY) {
					const firstDayOfWeek = this.accountService.firstDayOfWeek % 7; // moment accepts 0-6
					let startOfWeekTs = momentInstance.day(firstDayOfWeek).startOf('day').unix();
					if (firstTs && firstTs < startOfWeekTs) {
						// .day(x) sets to the current week (Sun 0 - Sat 6)
						// does not work ok if first ts (not matched) is f.ex. Sunday
						startOfWeekTs = momentInstance
							.day(firstDayOfWeek - 7)
							.startOf('day')
							.unix();
					}
					return startOfWeekTs;
				}
			}
		}
		return null;
	}

	private formatGranulationLabel(ts: number) {
		// simulates BE formatting of granulation labels
		// framework Databox/Service/Renderer/Util/RendererUtils.php::prepareGranulationLabelForTimestamp
		const granulationTimeUnit = this.granulationTimeUnit;
		if (!granulationTimeUnit) {
			return '';
		}
		let format = '';
		switch (granulationTimeUnit) {
			case TimeUnit.HOUR:
				format = 'h A'; // 12-hour format of an hour without leading zeros (9 AM; 12 PM; 3 PM)
				break;
			case TimeUnit.DAY:
				format = 'D'; // Day of the month without leading zeros
				break;
			case TimeUnit.WEEK:
				format = '[W]w'; // Week of year without leading zeros
				break;
			case TimeUnit.MONTH:
				format = 'MMM'; // Short name of month
				break;
			case TimeUnit.QUARTER:
				format = '[Q]Q'; // Quarter of year without leading zeros
				break;
			case TimeUnit.YEAR:
				format = 'YYYY'; // Full year
				break;
		}
		if (!format) {
			return '';
		}
		return getFormattedTime(ts, this.dataTimezone, format);
	}

	// ------- visualization switch helpers --------

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		// cleanup heatmap-specifics
		this.loggerService.logToConsole('[Vis change] from Heatmap: cleanup itemsInLine');
		this.block.itemsInLine = null;
	}

	// ------- MISC --------

	override getAllMainPoints() {
		const points: DChartPrimaryPoint[] = [];

		const seriePoints = this.series?.points;
		if (seriePoints?.length) {
			points.push(seriePoints[0]!);
		}

		return points;
	}

	static override recordLimit = RecordLimit;
}
